import React, { useState, useEffect, useContext } from 'react';
import {
  getDocs,
  query,
  where,
  collection,
  doc,
  updateDoc,
  orderBy,
  getDoc
} from 'firebase/firestore';
import {
  Card,
  Button,
  Collapse,
  Badge,
  Modal,
  Row,
  Col,
  Image
} from 'react-bootstrap';
import {
  CalendarDays,
  Clock,
  IndianRupee,
  Users,
  ChevronDown,
  ChevronUp,
  CircleCheckBig,
  CircleX,
  MapPin,
  TentTree,
  CalendarCheck2,
  Info,
  InfoIcon
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { db } from '../FireBase';
import '../css/OrderDetail.css';
import Lottie from 'lottie-react';
import noBookings from '../assets/noBookings.json';
import toast, { Toaster } from 'react-hot-toast';
import { Link } from 'react-router-dom';

const TourBookingList = () => {
  const { currentUser } = useContext(AuthContext);

  const [orders, setOrders] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    const fetchTourBookings = async () => {
      if (!currentUser) return;

      try {
        const q = query(
          collection(db, 'package_bookings'),
          where('userId', '==', currentUser.cusmid),
          orderBy('createdAt', 'desc')
        );

        const snap = await getDocs(q);
        const bookings = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOrders(bookings);
      } catch (err) {
        console.error('Error fetching package bookings:', err);
        toast.error('Failed to load bookings');
      }
    };

    fetchTourBookings();
  }, [currentUser]);

  useEffect(() => {
    fetchCancellationPolicy();
  }, []);

  const fetchCancellationPolicy = async () => {
    try {
      const ref = doc(db, 'settings', 'cancellation');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setCancellationPolicy(snap.data());
      }
    } catch (err) {
      console.error('Error fetching cancellation settings:', err);
    }
  };

  const getRefundPercentage = (order) => {
    if (!order || !order.checkIn) return 0;
    if (!cancellationPolicy?.packages?.enabled) return 0;

    const rules = cancellationPolicy.packages.rules || [];
    if (rules.length === 0) return 0;

    const formatDate = (ddmmyyyy) => {
      const [day, month, year] = ddmmyyyy.split('/');
      return `${year}-${month}-${day}`;
    };

    const startDate = new Date(formatDate(order.checkIn));
    const today = new Date();
    const todayNormalized = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const diffDays = Math.ceil(
      (startDate - todayNormalized) / (1000 * 60 * 60 * 24)
    );

    const sortedRules = [...rules].sort((a, b) => b.days - a.days);
    const matchingRule = sortedRules.find(rule => diffDays >= rule.days);

    if (matchingRule) return Number(matchingRule.refund);

    const lastRule = sortedRules[sortedRules.length - 1];
    return Number(lastRule?.refund || 0);
  };

  const handleCancel = async () => {
    if (!selectedOrder) return;

    setIsCancelling(true);

    try {
      const order = orders.find(o => o.id === selectedOrder.id);
      if (!order) return;

      const refundPercent = getRefundPercentage(order);
      const refundAmount = ((Number(order.total || 0)) * refundPercent) / 100;

      const ref = doc(db, 'package_bookings', selectedOrder.id);

      await updateDoc(ref, {
        status: 'cancelled',
        refundPercent,
        refundAmount,
      });

      setOrders(prev =>
        prev.map(o =>
          o.id === selectedOrder.id
            ? { ...o, status: 'cancelled', refundPercent, refundAmount }
            : o
        )
      );

      await fetch(
        'https://us-central1-kabili.cloudfunctions.net/api/send-cancellation-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: order.orderId,
            refundAmount,
            refundPercent,
          }),
        }
      );

      toast.success(`Booking cancelled. Refund: ₹${refundAmount} (${refundPercent}%)`);
    } catch (err) {
      console.error(err);
      toast.error('Error cancelling booking');
    } finally {
      setShowConfirmModal(false);
      setSelectedOrder(null);
      setIsCancelling(false);
    }
  };

  const toggleCollapse = index => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CircleCheckBig className="me-2 text-success" size={22} />;
      case 'cancelled':
        return <CircleX className="me-2 text-danger" size={22} />;
      case 'booking-confirmed':
        return <CalendarCheck2 className="me-2 text-success" size={22} />;
      case 'payment-pending':
        return <Clock className="me-2 text-warning" size={22} />;
      default:
        return <Clock className="me-2 text-warning" size={22} />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'booking-confirmed':
        return 'success';
      case 'payment-pending':
        return 'warning';
      case 'cancelled':
        return 'danger';
      case 'checked-in':
      case 'checked-out':
        return 'secondary';
      default:
        return 'dark';
    }
  };

  if (orders.length === 0) {
    return (
      <div
        className="empty-order-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <div className="lottie-wrapper mb-3" style={{ width: 250 }}>
          <Lottie animationData={noBookings} loop={true} />
        </div>
        <p className="empty-order-text">No package bookings yet!</p>
      </div>
    );
  }

  return (
    <div>
      <Toaster position="top-center" />

      {orders.map((order, index) => {
        const packageImage = order.packageImage || '';
        const selectedDays = Number(order.selectedDays || order.stayDays || 0);
        const selectedMembers = Number(order.selectedMembers || order.persons || 0);
        const pricePerPerson = Number(order.pricePerPerson || 0);
        const subtotal = Number(
          order.subtotal ??
          (pricePerPerson * selectedMembers * selectedDays)
        );
        const discount = Number(order.discount || 0);
        const total = Number(order.total || 0);

        return (
          <Card key={order.id} className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center rounded-top-4">
              <div>
                <h5 className="mb-0 d-flex align-items-center" style={{ color: '#54b435' }}>
                  <MapPin className="me-2" /> {order.packageName}
                </h5>
                <small className="text-muted">
                  <CalendarDays size={16} className="me-1" />
                  Booking ID: {order.orderId}
                </small>
              </div>
              {getStatusIcon(order.status)}
            </Card.Header>

            <Card.Body>
              <Row className="g-3 align-items-center">
                {packageImage && (
                  <Col md={3}>
                    <Image
                      src={packageImage}
                      alt={order.packageName}
                      rounded
                      fluid
                      style={{ height: 120, width: '100%', objectFit: 'cover' }}
                    />
                  </Col>
                )}

                <Col md={packageImage ? 9 : 12}>
                  <Row>
                    <Col md={6}>
                      <p>
                        <CalendarDays size={18} className="me-2 text-primary" />
                        From: <strong>{order.checkIn}</strong>
                      </p>
                      <p>
                        <CalendarDays size={18} className="me-2 text-danger" />
                        To: <strong>{order.checkOut}</strong>
                      </p>
                      <p>
                        <TentTree className="me-2 text-info" />
                        Days: <strong>{selectedDays}</strong>
                      </p>
                    </Col>

                    <Col md={6}>
                      <p>
                        <Users className="me-2 text-success" />
                        Guests: <strong>{selectedMembers}</strong>
                      </p>
                      <p>
                        <IndianRupee className="me-2" />
                        Price / person / day: <strong>₹{pricePerPerson}</strong>
                      </p>
                      <p>
                        <IndianRupee className="me-2" />
                        Total: <strong>₹{total}</strong>
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Button
                size="sm"
                className="mt-3 btn-own-2"
                onClick={() => toggleCollapse(index)}
              >
                {openIndex === index ? (
                  <ChevronUp className="me-1" />
                ) : (
                  <ChevronDown className="me-1" />
                )}
                {openIndex === index ? 'Hide Details' : 'View Details'}
              </Button>

              <Collapse in={openIndex === index}>
                <div className="mt-4">
                  <h6>Booking Summary</h6>
                  <p>Payment method: {order.paymentMethod}</p>
                  <p>Price per person per day: ₹{pricePerPerson}</p>
                  <p>Guests: {selectedMembers}</p>
                  <p>Days: {selectedDays}</p>
                  <p>Subtotal: ₹{subtotal}</p>
                  <p>Discount: ₹{discount}</p>
                  <p><strong>Total: ₹{total}</strong></p>

                  <p>
                    <strong>Status: </strong>
                    <Badge bg={getStatusBadge(order.status)}>
                      {order.status}
                    </Badge>
                  </p>

                  {order.guests?.length > 0 && (
                    <div className="mt-3">
                      <h6>Guest Details</h6>
                      {order.guests.map((guest, guestIndex) => (
                        <div
                          key={guestIndex}
                          className="d-flex justify-content-between align-items-center border rounded-3 px-3 py-2 mb-2"
                        >
                          <span>{guest.name}</span>
                          <span className="text-muted">{guest.phone}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {order.status === 'cancelled' && (
                    <div
                      className="mt-3 p-3"
                      style={{ backgroundColor: '#ffe6e6', borderRadius: '15px' }}
                    >
                      <h6 className="text-danger">Cancellation Details</h6>

                      <p className="mb-1">
                        <strong>Refund Percentage:</strong> {order.refundPercent || 0}%
                      </p>

                      <p className="mb-1">
                        <strong>Refund Amount:</strong> ₹{order.refundAmount || 0}
                      </p>

                      <p style={{ fontSize: '12px' }}>
                        <InfoIcon size={15} color="red" /> Refund will be initiated in 3 business days. For refund-related queries, please contact us.
                      </p>
                    </div>
                  )}

                  {order.status !== 'cancelled' && cancellationPolicy?.packages?.enabled && (
                    <div className="d-flex flex-column align-items-center mt-3">
                      <p className="text-muted mb-2">
                        {getRefundPercentage(order) > 0
                          ? `If you cancel now, you will receive a ${getRefundPercentage(order)}% refund.`
                          : 'Cancellation not refundable.'}
                      </p>

                      <Button
                        className="w-100 mt-1 btn-own-danger"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowConfirmModal(true);
                        }}
                      >
                        Cancel Booking
                      </Button>

                      <br />

                      <Link
                        to="/cancellation-policy"
                        className="text-decoration-none text-primary mb-2"
                      >
                        <Info className="me-1" size={16} /> Read Cancellation Policy
                      </Link>
                    </div>
                  )}

                  {order.status !== 'cancelled' && !cancellationPolicy?.packages?.enabled && (
                    <p className="text-muted mt-3">
                      <em>Cancellation not available for this booking.</em>
                    </p>
                  )}
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        );
      })}

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancellation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Are you sure you want to cancel this package booking?
          <br />
          <strong>
            Refund: {selectedOrder ? getRefundPercentage(selectedOrder) : 0}%
          </strong>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            No
          </Button>

          <Button variant="danger" onClick={handleCancel} disabled={isCancelling}>
            {isCancelling ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Cancelling...
              </>
            ) : (
              'Yes, Cancel'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TourBookingList;